import L from "leaflet";
import { MapSnap } from "./MapSnap";
import "leaflet-geometryutil";
import type { Position } from "geojson";
import { fx_redondearCoordenada } from "./AoiPolygonUnion";

/** Motor de la línea de corte del paso 4 (dividir en lotes).
 *  **Sin `leaflet-draw`**: se resuelve con eventos nativos de Leaflet, misma
 *  metodología que la app de referencia externa
 *  (`referencias_codigo_externo/App`, `src/proyectos/lib/dibujoMapa.ts` —
 *  herramienta `linea`) y que el motor del paso 3 (`AoiDrawEngine`).
 *
 *  Mecánica del trazo, igual que la referencia:
 *   - click en el mapa → agrega un vértice;
 *   - click sobre el ÚLTIMO vértice → cierra la línea (en el polígono del
 *     paso 3 cierra el PRIMERO; en una línea abierta es el último);
 *   - el cursor arrastra una guía en vivo hasta el próximo vértice.
 *
 *  Trae las mismas defensas que se le fueron poniendo al paso 3, porque los
 *  problemas son los mismos:
 *   - `mousedown`/`mouseup` con tolerancia en vez de `click`: el navegador
 *     solo dispara `click` si la bajada y la subida caen en el mismo
 *     elemento, y el arrastre del mapa mueve los tiles bajo el cursor, así
 *     que se perdían clicks;
 *   - la guía se REUSA con `setLatLngs` en vez de recrearla en cada
 *     `mousemove`;
 *   - durante la animación de zoom la guía no se repinta: hacerlo la
 *     proyectaría a un zoom intermedio mientras el renderer está a medio
 *     `transform`.
 *
 *  Conserva el SNAP, que es funcional y no cosmético: el algoritmo de corte
 *  necesita 2 intersecciones reales con el polígono (ver `LoteSplitGeometria`
 *  del servidor), y sin snap una línea que no toca el borde con precisión
 *  exacta hace fallar el corte en silencio.
 *
 *  **Contra qué snapea.** Contra los bordes del lote que el usuario tocó — no
 *  contra todas las piezas del modelo. El corte se va a aplicar sobre ESE lote,
 *  así que lo que tiene que quedar exacto son sus vértices. Hasta el 2026-10-27
 *  ese lote se leía de la base por gRPC al elegirlo (`LotesWfsService`); desde
 *  entonces sale de la copia del estado, que es la de la base (ver
 *  `LoteSplitToolbar.comenzarCorteDeAsync`). */

/** Línea de corte temporal — mismo estilo que el control original (roja
 *  punteada), nunca queda como capa persistida: se descarta apenas se usa
 *  para partir (o falla) la pieza que cruza. */
const SPLIT_LINE_STYLE: L.PathOptions = {
  color: "#e1004c",
  weight: 2,
  opacity: 0.9,
  dashArray: "6 4",
};

/** Vértice del trazo: mismo círculo blanco con borde naranja que el resto
 *  del wizard (`custom-draw-vertex`, en `aoiMap.css`). `className` lleva LAS
 *  DOS clases porque `L.DivIcon` trae `leaflet-div-icon` por defecto y pasar
 *  uno propio lo REEMPLAZA en vez de sumarse. */
const SPLIT_VERTEX_ICON = new L.DivIcon({
  iconSize: [12, 12],
  className: "leaflet-div-icon custom-draw-vertex",
});

/** Vértice de CIERRE (el último puesto): relleno NARANJA sólido, contra el
 *  blanco de los demás, para que se vea de un golpe dónde termina el trazo.
 *  Desde el 2º vértice lleva además el tooltip "Finalizar aquí". */
const SPLIT_VERTEX_CIERRE_ICON = new L.DivIcon({
  iconSize: [12, 12],
  className: "leaflet-div-icon custom-draw-vertex custom-draw-vertex--fin",
});

/** A partir de cuántos vértices se muestra el tooltip de cierre. Con uno
 *  solo no tiene sentido: una línea de un punto no se puede finalizar. */
const VERTICES_PARA_TOOLTIP_FIN = 2;

/** Cuánto se puede mover el cursor entre `mousedown` y `mouseup` para que
 *  igual cuente como click y no como un paneo (ver el doc de la clase). */
const CLICK_TOLERANCIA_PX = 5;

/** Tolerancia para cerrar la línea haciendo click CERCA del último vértice,
 *  además de encima de su marcador. */
const CIERRE_TOLERANCIA_PX = 14;

export interface LoteSplitLineEngineOpciones {
  map: L.Map;
  /** Se cerró una línea. Llega en coordenadas GeoJSON, tal como se trazó: la
   *  prolongación de los extremos y el corte los hace el servidor (ver
   *  `LoteSplitToolbar._onLineaTrazada`). */
  onLineaTrazada: (lineCoords: Position[]) => void;
}

export class LoteSplitLineEngine {
  constructor(opciones: LoteSplitLineEngineOpciones) {
    this._opciones = opciones;
    this._map = opciones.map;
    this._snap = new MapSnap(this._map);
  }

  get activo(): boolean {
    return this._activo;
  }

  /** Enciende el trazo. `bordes` son los anillos de las piezas actuales, para
   *  el snap — se pasan al activar y no se mantienen al día en vivo, porque
   *  solo importan mientras se dibuja. */
  activar(bordes: L.LatLng[][]): void {
    if (this._activo) return;
    this._activo = true;
    this._armarGuiaDeSnap(bordes);

    this._map.on("mousedown", this._alMouseDown);
    this._map.on("mouseup", this._alMouseUp);
    this._map.on("mousemove", this._alMover);
    this._map.on("zoomstart", this._alZoomStart);
    this._map.on("zoomend", this._alZoomEnd);
    this._map.doubleClickZoom.disable();
    this._map.getContainer().classList.add(CLASE_DIBUJANDO);
  }

  /** Rehace la guía de snap con las piezas actuales. Hace falta porque la
   *  herramienta YA NO se apaga sola al terminar un corte: tras dividir (o
   *  unir) las piezas son otras, y la guía armada al activar quedó vieja. */
  refrescarBordes(bordes: L.LatLng[][]): void {
    if (!this._activo) return;
    this._armarGuiaDeSnap(bordes);
  }

  desactivar(): void {
    if (!this._activo) return;
    this._activo = false;
    this._cancelarTrazo();
    this._quitarGuiaDeSnap();

    this._map.off("mousedown", this._alMouseDown);
    this._map.off("mouseup", this._alMouseUp);
    this._map.off("mousemove", this._alMover);
    this._map.off("zoomstart", this._alZoomStart);
    this._map.off("zoomend", this._alZoomEnd);
    this._map.doubleClickZoom.enable();
    this._map.getContainer().classList.remove(CLASE_DIBUJANDO);
  }

  destruir(): void {
    this.desactivar();
  }

  private readonly _opciones: LoteSplitLineEngineOpciones;
  private readonly _map: L.Map;
  private _activo = false;

  private _vertices: L.LatLng[] = [];
  private _marcadores: L.Marker[] = [];
  private _previa: L.Polyline | null = null;
  /** El pegado a los bordes; se arma en el constructor, cuando `_map` ya está. */
  private _snap!: MapSnap;

  private _puntoBajada: L.Point | null = null;
  private _cursorEnPantalla: L.Point | null = null;
  private _zoomEnCurso = false;

  // --- Eventos ------------------------------------------------------------

  private _alMouseDown = (e: L.LeafletMouseEvent): void => {
    this._puntoBajada = e.containerPoint;
  };

  private _alMouseUp = (e: L.LeafletMouseEvent): void => {
    const bajada = this._puntoBajada;
    this._puntoBajada = null;
    if (!bajada) return;
    if (bajada.distanceTo(e.containerPoint) > CLICK_TOLERANCIA_PX) return;

    const latlng = this._conSnap(e.latlng);

    // Red de seguridad del cierre: un click CERCA del último vértice también
    // termina la línea, no solo uno exactamente sobre su marcador.
    if (this._vertices.length >= 2 && this._cercaDelUltimoVertice(latlng)) {
      this._cerrarLinea();
      return;
    }

    this._agregarVertice(latlng);
  };

  private _alMover = (e: L.LeafletMouseEvent): void => {
    this._cursorEnPantalla = e.containerPoint;

    // El indicador va ANTES de los cortes de abajo: tiene que verse también
    // con la línea todavía sin empezar, para que el usuario sepa dónde va a
    // caer el primer punto.
    const snap = this._buscarSnap(e.latlng);
    this._mostrarSnap(snap);

    if (this._vertices.length === 0) return;
    if (this._zoomEnCurso) return;
    this._repintarPrevia(snap ?? e.latlng);
  };

  private _alZoomStart = (): void => {
    this._zoomEnCurso = true;
  };

  private _alZoomEnd = (): void => {
    this._zoomEnCurso = false;
    if (!this._cursorEnPantalla || this._vertices.length === 0) return;
    this._repintarPrevia(
      this._map.containerPointToLatLng(this._cursorEnPantalla),
    );
  };

  // --- Trazo --------------------------------------------------------------

  private _agregarVertice(latlng: L.LatLng): void {
    // Mismo redondeo que el paso 3, y por la misma razón: la línea de corte
    // tiene que hablar el mismo idioma numérico que los polígonos que cruza.
    this._vertices.push(
      L.latLng(
        fx_redondearCoordenada(latlng.lat),
        fx_redondearCoordenada(latlng.lng),
      ),
    );
    const indice = this._vertices.length - 1;

    const marcador = L.marker(latlng, {
      icon: SPLIT_VERTEX_ICON,
      keyboard: false,
      zIndexOffset: 1000,
    });
    marcador.on("click", (ev: L.LeafletMouseEvent) => {
      L.DomEvent.stop(ev);
      // Solo el ÚLTIMO vértice cierra, y cuál es el último cambia a medida
      // que se agregan más — de ahí que se compare contra la longitud actual.
      if (indice === this._vertices.length - 1) this._cerrarLinea();
    });
    marcador.addTo(this._map);
    this._marcadores.push(marcador);

    this._marcarVerticeDeCierre();
    this._repintarPrevia(latlng);
  }

  /** Solo el último marcador lleva el ícono naranja de cierre y, desde el
   *  2º vértice, el tooltip que indica dónde finalizar. Los anteriores
   *  vuelven al ícono normal: cuál es "el último" cambia en cada click. */
  private _marcarVerticeDeCierre(): void {
    const ultimo = this._marcadores.length - 1;
    const mostrarTooltip = this._vertices.length >= VERTICES_PARA_TOOLTIP_FIN;

    this._marcadores.forEach((marcador, i) => {
      const esUltimo = i === ultimo;
      marcador.setIcon(esUltimo ? SPLIT_VERTEX_CIERRE_ICON : SPLIT_VERTEX_ICON);

      marcador.unbindTooltip();
      if (esUltimo && mostrarTooltip) {
        marcador.bindTooltip("Finalizar aquí", {
          permanent: true,
          direction: "top",
          offset: [0, -8],
          className: "lote-split-fin-tooltip",
        });
      }
    });
  }

  private _cercaDelUltimoVertice(latlng: L.LatLng): boolean {
    const ultimo = this._vertices[this._vertices.length - 1];
    if (!ultimo) return false;
    return (
      this._map
        .latLngToContainerPoint(ultimo)
        .distanceTo(this._map.latLngToContainerPoint(latlng)) <=
      CIERRE_TOLERANCIA_PX
    );
  }

  /** Redibuja la guía: vértices fijos + la posición del cursor. Se REUSA la
   *  capa (`setLatLngs`) en vez de recrearla, y va `interactive: false` para
   *  que no capture el click que debe llegar al mapa o al marcador. */
  private _repintarPrevia(cursor: L.LatLng): void {
    if (this._vertices.length === 0) return;
    const puntos = [...this._vertices, cursor];

    if (this._previa) {
      this._previa.setLatLngs(puntos);
      return;
    }
    this._previa = L.polyline(puntos, {
      ...SPLIT_LINE_STYLE,
      interactive: false,
    }).addTo(this._map);
  }

  private _cerrarLinea(): void {
    if (this._vertices.length < 2) return;
    const lineCoords: Position[] = this._vertices.map((v) => [v.lng, v.lat]);
    this._cancelarTrazo();
    this._opciones.onLineaTrazada(lineCoords);
  }

  private _cancelarTrazo(): void {
    this._vertices = [];
    for (const marcador of this._marcadores) this._map.removeLayer(marcador);
    this._marcadores = [];
    if (this._previa) {
      this._map.removeLayer(this._previa);
      this._previa = null;
    }
    this._puntoBajada = null;
    this._zoomEnCurso = false;
    this._ocultarSnap();
  }

  // --- Snap ---------------------------------------------------------------
  // La implementación vive en `MapSnap`, compartida con el dibujo de polígonos
  // (`AoiDrawEngine`). Acá quedan solo los envoltorios que usa el resto de la
  // clase, con los nombres que ya tenían.

  private _armarGuiaDeSnap(bordes: L.LatLng[][]): void {
    this._snap.setBordes(bordes);
  }

  private _quitarGuiaDeSnap(): void {
    this._snap.setBordes([]);
  }

  private _buscarSnap(latlng: L.LatLng): L.LatLng | null {
    return this._snap.buscar(latlng);
  }

  private _conSnap(latlng: L.LatLng): L.LatLng {
    return this._snap.aplicar(latlng);
  }

  private _mostrarSnap(latlng: L.LatLng | null): void {
    this._snap.mostrar(latlng);
  }

  private _ocultarSnap(): void {
    this._snap.ocultar();
  }
}

/** Cursor de mano mientras la herramienta está activa (misma clase y mismo
 *  motivo que en el paso 3, ver `aoiMap.css`). */
const CLASE_DIBUJANDO = "aoi-dibujando";
